//////////////////////////////////////////////////////////////////////////////
//
//  사용자 정보 팝업
//
//  작성자: MediInStep Team
//  생성일: 2025-01-XX
//
//  설명: 사용자 정보를 보여주는 팝업 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Card from "@mui/material/Card";
import { request } from "app/network";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { useMaterialUIController, MaterialUI } from "context";
import { useTheme, ThemeProvider } from "@mui/material/styles";

import QRCode from "qrcode";
import UserActivityDialog from "./UserActivityDialog";
import PointGrantDialog from "./PointGrantDialog";

interface UserDetailDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  darkMode?: boolean;
  theme?: any; // Material-UI theme
  onUpdateSuccess?: () => void;
}

interface UserDetailDialogState {
  loading: boolean;
  user: any | null;
  darkMode: boolean; // 다크 모드 상태 저장
  qrCodeSvg: string | null; // QR 코드 SVG 문자열
  // Edit mode states
  isEditing: boolean;
  editEmail: string;
  editMobile: string;
  editStepPolicySeqIds: number[];
  editVideoPolicySeqIds: number[];
  editUsePolicySeqIds: number[];
  editPointLimitPolicySeqIds: number[];
  editChallengePolicySeqIds: number[];
  // Available policies for selection
  stepPolicies: any[];
  videoPolicies: any[];
  usePolicies: any[];
  challengePolicies: any[];
  // dailyLimitPolicies removed

  // User Point Limit Policies
  userPointLimitPolicies: any[];
  allPointLimitPolicies: any[]; // For selection
  loadingPolicies: boolean;

  // Activity Dialog State
  openActivityDialog: boolean;

  // Point Grant Dialog State
  openPointGrantDialog: boolean;

  // Surgery Site State
  availableSurgerySites: any[];
  editLiveSurgerySites: Array<{ seqId: number, surgerySiteSeqId: number, siteName: string, date: Date | null }>;
  editSelectedSurgerySite: any | null;
  editSelectedSurgeryDate: Date | null;

  // Interest Area State
  availableBodyParts: any[];
  interestAreaCategories: any[];
  editInterestBodyPartIds: number[];
}

class UserDetailDialogClass extends Component<UserDetailDialogProps, UserDetailDialogState> {
  static contextType = MaterialUI;

  constructor(props: UserDetailDialogProps) {
    super(props);
    this.state = {
      loading: false,
      user: null, // 초기값 null 명시
      darkMode: false, // 초기값
      qrCodeSvg: null, // QR 코드 SVG 초기화
      // Edit mode initial values
      isEditing: false,
      editEmail: "",
      editMobile: "",
      editStepPolicySeqIds: [],
      editVideoPolicySeqIds: [],
      editUsePolicySeqIds: [],
      editPointLimitPolicySeqIds: [],
      editChallengePolicySeqIds: [],

      // User Point Limit Policies
      userPointLimitPolicies: [],
      allPointLimitPolicies: [],
      // Surgery Site State
      availableSurgerySites: [],
      editLiveSurgerySites: [],
      editSelectedSurgerySite: null,
      editSelectedSurgeryDate: null,
      // Interest Area State
      availableBodyParts: [],
      interestAreaCategories: [],
      editInterestBodyPartIds: [],
      // Available policies
      stepPolicies: [],
      videoPolicies: [],
      usePolicies: [],
      challengePolicies: [],

      // dailyLimitPolicies removed
      loadingPolicies: false,
      // Activity Dialog State
      openActivityDialog: false,
      // Point Grant Dialog State
      openPointGrantDialog: false,
    };
  }

  componentDidMount() {
    // Context에서 darkMode 가져와서 state에 저장
    const context = this.context as any;
    if (context?.darkMode !== undefined) {
      this.setState({ darkMode: context.darkMode });
    } else if (this.props.darkMode !== undefined) {
      this.setState({ darkMode: this.props.darkMode });
    }
  }


  componentDidUpdate(prevProps: UserDetailDialogProps, prevState: UserDetailDialogState) {
    // Context에서 darkMode 변경 감지
    const context = this.context as any;
    const currentDarkMode = context?.darkMode ?? this.props.darkMode ?? false;
    if (currentDarkMode !== this.state.darkMode) {
      this.setState({ darkMode: currentDarkMode });
    }

    // 다이얼로그가 열리고 userId가 변경되면 사용자 정보 로드
    if (this.props.open && !prevProps.open && this.props.userId) {
      this.loadUserDetail();
      this.loadPolicies();
    }

    // 다이얼로그가 닫히면 상태 초기화
    if (!this.props.open && prevProps.open) {
      this.setState({ user: null, qrCodeSvg: null });
    }

    // 사용자 정보나 다크 모드가 변경되면 QR 코드 재생성
    const loginType = (this.state.user?.loginType || this.state.user?.LoginType || "").toLowerCase();
    const isQrUser = loginType === "qr" || loginType === "login_qr";

    if (this.state.user && isQrUser && (this.state.user.qrToken || this.state.user.QrToken)) {
      const qrToken = this.state.user.qrToken || this.state.user.QrToken;
      if (prevState.darkMode !== this.state.darkMode || prevState.user?.qrToken !== qrToken) {
        QRCode.toString(qrToken, {
          type: "svg",
          width: 200,
          margin: 2,
          color: {
            dark: this.state.darkMode ? "#ffffff" : "#000000",
            light: this.state.darkMode ? "#1a1a1a" : "#ffffff",
          },
        })
          .then((svg) => {
            this.setState({ qrCodeSvg: svg });
          })
          .catch((error) => {
            console.error("QR 코드 생성 실패:", error);
            this.setState({ qrCodeSvg: null });
          });
      }
    }
  }

  loadUserDetail = async () => {
    if (!this.props.userId) return;

    try {
      this.setState({ loading: true });
      const response = await request("User.getById", {
        paramz: {},
        pathParams: { id: this.props.userId },
      });

      if (response && response.data) {
        console.log("사용자 상세 정보 응답:", response.data);
        const userData = response.data;
        this.setState({ user: userData });

        // QR 로그인 타입 및 토큰 확인 (디버깅용)
        const loginType = (userData.loginType || userData.LoginType || "").toLowerCase();
        const qrToken = userData.qrToken || userData.QrToken;

        console.log("DEBUG: UserDetailDialog loadUserDetail - loginType:", loginType, "qrToken:", qrToken);

        // QR 코드 생성은 componentDidUpdate에서 처리됨 (user state 변경 감지)
      } else {
        console.error("사용자 상세 정보 조회 실패: 응답 데이터가 없습니다.", response);
      }
    } catch (error: any) {
      console.error("사용자 상세 정보 조회 실패:", error);
      console.error("에러 상세:", error.response?.data || error.message);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadPolicies = async () => {
    try {
      this.setState({ loadingPolicies: true });

      // Load all policy types and surgery sites in parallel
      const [allEarnResponse, useResponse, limitResponse, surgerySitesResponse, bodyPartsResponse, categoriesResponse] = await Promise.all([
        request("PointPolicy.list", { paramz: {} }),
        request("PointUsePolicy.list", { paramz: {} }),
        request("PointLimitPolicy.list", { paramz: {} }), // Changed to PointLimitPolicy.list
        request("SurgerySite.pagedList", { paramz: { PageNumber: 1, PageSize: 100 } }),
        request("BodyPart.getAll", { paramz: {} }),
        request("CommonCode.getByGroup", { pathParams: { groupCode: "INTEREST_AREA_CATEGORY" } }),
      ]);

      // Filter earning policies by conditionType
      const allEarnPolicies = allEarnResponse?.data || [];
      console.log("DEBUG: allEarnPolicies", allEarnPolicies);
      const stepPolicies = allEarnPolicies.filter((p: any) => (p.policyType === "STEP_EARN" || p.PolicyType === "STEP_EARN"));
      const videoPolicies = allEarnPolicies.filter((p: any) => (
        p.conditionType === "VIDEO_COMPLETE" ||
        p.policyType === "VIDEO_EARN" || p.PolicyType === "VIDEO_EARN" ||
        p.policyType === "DAILY_VIDEO_COUNT" || p.PolicyType === "DAILY_VIDEO_COUNT" ||
        p.policyType === "VIDEO_DAILY_COUNT" || p.PolicyType === "VIDEO_DAILY_COUNT"
      ));
      const challengePolicies = allEarnPolicies.filter((p: any) => (
        p.policyType === "CHALLENGE_EARN" || p.PolicyType === "CHALLENGE_EARN" ||
        p.policyType === "MONTHLY_CHALLENGE" || p.PolicyType === "MONTHLY_CHALLENGE"
      ));
      console.log("DEBUG: challengePolicies filtered", challengePolicies);
      const usePolicies = useResponse?.data || [];
      const allPointLimitPolicies = limitResponse?.data || [];
      const availableSurgerySites = surgerySitesResponse?.data?.items || [];

      this.setState({
        stepPolicies,
        videoPolicies,
        usePolicies,
        challengePolicies,
        allPointLimitPolicies,
        availableSurgerySites,
        availableBodyParts: bodyPartsResponse?.data || [],
        interestAreaCategories: categoriesResponse?.data || [],
      });

      console.log("정책 및 메타데이터 로드 완료:", { stepPolicies, videoPolicies, usePolicies, allPointLimitPolicies, availableSurgerySites, availableBodyParts: bodyPartsResponse?.data, interestAreaCategories: categoriesResponse?.data });
    } catch (error) {
      console.error("정책 로드 실패:", error);
    } finally {
      this.setState({ loadingPolicies: false });
    }
  };

  handleEdit = async () => {
    const { user } = this.state;
    if (!user) return;

    // Map existing surgery sites
    const mappedSurgerySites = (user.surgerySites || []).map((s: any) => {
      const siteInfo = this.state.availableSurgerySites.find(as => as.seqId === s.surgerySiteSeqId);
      return {
        seqId: s.seqId || 0,
        surgerySiteSeqId: s.surgerySiteSeqId,
        siteName: siteInfo?.siteName || `Unknown (${s.surgerySiteSeqId})`,
        date: s.surgeryDate ? new Date(s.surgeryDate) : null
      };
    });

    // Initialize edit values from current user data
    this.setState({
      isEditing: true,
      editEmail: user.email || "",
      editMobile: user.mobile || "",
      editStepPolicySeqIds: user.stepPolicies?.map((p: any) => p.seqId) || [],
      editVideoPolicySeqIds: user.videoPolicies?.map((p: any) => p.seqId) || [],
      editUsePolicySeqIds: user.usePolicies?.map((p: any) => p.seqId) || [],
      editPointLimitPolicySeqIds: user.pointLimitPolicies?.map((p: any) => p.seqId) || [],
      editChallengePolicySeqIds: (user.challengePolicies || user.ChallengePolicies)?.map((p: any) => p.seqId) || [],
      editLiveSurgerySites: mappedSurgerySites,
      editInterestBodyPartIds: user.interestBodyPartIds || [],
    });
  };

  handleCancelEdit = () => {
    this.setState({
      isEditing: false,
      editEmail: "",
      editMobile: "",
      editStepPolicySeqIds: [],
      editVideoPolicySeqIds: [],
      editUsePolicySeqIds: [],
      editPointLimitPolicySeqIds: [],
      editChallengePolicySeqIds: [],
      editLiveSurgerySites: [],
      editSelectedSurgerySite: null,
      editSelectedSurgeryDate: null,
      editInterestBodyPartIds: [],
    });
  };

  handleAddSurgerySite = () => {
    const { editSelectedSurgerySite, editSelectedSurgeryDate, editLiveSurgerySites } = this.state;
    if (!editSelectedSurgerySite) return;

    // Check duplicate - REMOVED to allow multiples
    // if (editLiveSurgerySites.some(s => s.seqId === editSelectedSurgerySite.seqId)) {
    //   alert("이미 추가된 수술 부위입니다.");
    //   return;
    // }

    this.setState({
      editLiveSurgerySites: [
        ...editLiveSurgerySites,
        {
          seqId: 0,
          surgerySiteSeqId: editSelectedSurgerySite.seqId,
          siteName: editSelectedSurgerySite.siteName,
          date: editSelectedSurgeryDate
        }
      ],
      editSelectedSurgerySite: null,
      editSelectedSurgeryDate: null
    });
  };

  handleRemoveSurgerySite = (index: number) => {
    this.setState({
      editLiveSurgerySites: this.state.editLiveSurgerySites.filter((_, i) => i !== index)
    });
  };

  handleSaveEdit = async () => {
    const { user, editEmail, editMobile, editStepPolicySeqIds, editVideoPolicySeqIds, editUsePolicySeqIds, editPointLimitPolicySeqIds, editChallengePolicySeqIds, editLiveSurgerySites, editInterestBodyPartIds } = this.state;
    if (!user) return;

    try {
      this.setState({ loading: true });

      // Use this.props.userId which is the actual user ID passed from parent
      await request("User.update", {
        pathParams: { id: this.props.userId },
        paramz: {
          email: editEmail,
          mobile: editMobile,
          stepPolicySeqIds: editStepPolicySeqIds,
          videoPolicySeqIds: editVideoPolicySeqIds,
          usePolicySeqIds: editUsePolicySeqIds,
          pointLimitPolicySeqIds: editPointLimitPolicySeqIds,
          challengePolicySeqIds: editChallengePolicySeqIds,
          surgerySites: editLiveSurgerySites.map(s => ({
            seqId: s.seqId,
            surgerySiteSeqId: s.surgerySiteSeqId,
            surgeryDate: s.date,
            comment: null as string | null
          })),
          interestBodyPartIds: editInterestBodyPartIds,
        },
      });

      // Reload user data
      await this.loadUserDetail();

      // Exit edit mode
      this.setState({ isEditing: false });

      // Show success message
      alert("사용자 정보가 수정되었습니다.");

      // Notify parent to refresh list
      if (this.props.onUpdateSuccess) {
        this.props.onUpdateSuccess();
      }
    } catch (error) {
      console.error("사용자 정보 수정 실패:", error);
      alert("사용자 정보 수정에 실패했습니다.");
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { open, onClose, userId, theme } = this.props;
    const { loading, user, darkMode } = this.state;

    // 테마에서 배경색 가져오기 (다른 팝업들과 동일)
    const backgroundColor = darkMode
      ? (theme?.palette?.background?.default || '#1a1a1a')
      : (theme?.palette?.background?.paper || '#ffffff');

    const cardBg = darkMode
      ? (theme?.palette?.background?.paper || '#2d2d2d')
      : (theme?.palette?.background?.paper || '#ffffff');

    const borderColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: backgroundColor,
            borderRadius: 2,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogContent
          sx={{
            backgroundColor: backgroundColor,
            padding: "32px",
          }}
        >
          {loading ? (
            <MDBox p={3} display="flex" justifyContent="center">
              <MDTypography>로딩 중...</MDTypography>
            </MDBox>
          ) : user ? (
            <MDBox>
              {/* 프로필 헤더 */}
              <MDBox
                sx={{
                  backgroundColor: cardBg,
                  borderRadius: 2,
                  padding: 3,
                  mb: 3,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDAvatar
                    src={user.profileImageUrl}
                    alt={user.name || "User"}
                    size="xl"
                    sx={{
                      width: 80,
                      height: 80,
                      border: `3px solid ${borderColor}`,
                      boxShadow: darkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                  </MDAvatar>
                  <MDBox flex={1}>
                    <MDTypography variant="h4" fontWeight="bold" mb={1}>
                      {user.name || user.id || "-"}
                      {user.id && (
                        <MDTypography component="span" variant="body2" color="text" sx={{ opacity: 0.7, ml: 1 }}>
                          ({user.id})
                        </MDTypography>
                      )}
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <MDBadge
                        badgeContent={user.enable ? "활성" : "비활성"}
                        color={user.enable ? "success" : "error"}
                        variant="gradient"
                        size="sm"
                      />
                    </MDBox>
                  </MDBox>

                  {/* 활동 기록 버튼 */}
                  <MDButton
                    variant="outlined"
                    color={darkMode ? "white" : "info"}
                    size="small"
                    onClick={() => {
                      this.setState({ openActivityDialog: true });
                    }}
                    startIcon={<Icon>history</Icon>}
                  >
                    활동 기록
                  </MDButton>

                  {/* Activity Statistics Dialog */}
                  <UserActivityDialog
                    open={this.state.openActivityDialog}
                    onClose={() => this.setState({ openActivityDialog: false })}
                    userId={this.props.userId || ""}
                  />

                  {/* 포인트 지급 버튼 */}
                  <MDButton
                    variant="outlined"
                    color={darkMode ? "white" : "success"}
                    size="small"
                    onClick={() => {
                      this.setState({ openPointGrantDialog: true });
                    }}
                    startIcon={<Icon>monetization_on</Icon>}
                    sx={{ ml: 1 }}
                  >
                    포인트 지급
                  </MDButton>

                  {/* Point Grant Dialog */}
                  {user && user.seqId && (
                    <PointGrantDialog
                      open={this.state.openPointGrantDialog}
                      onClose={() => this.setState({ openPointGrantDialog: false })}
                      userSeqId={user.seqId}
                      onSuccess={() => {
                        this.loadUserDetail(); // Reload user detail to update points/stats if needed
                        // Optionally trigger other updates
                      }}
                    />
                  )}
                </MDBox>
              </MDBox>

              {/* 2열 그리드: 기본 정보 + 역할 및 권한 */}
              <Grid container spacing={3} mb={3}>
                {/* 기본 정보 */}
                <Grid item xs={12} md={6}>
                  <MDBox
                    sx={{
                      backgroundColor: cardBg,
                      borderRadius: 2,
                      padding: 3,
                      height: '100%',
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <MDBox display="flex" alignItems="center" mb={3}>
                      <Icon sx={{ fontSize: '1.5rem', color: '#2196f3', mr: 1 }}>person</Icon>
                      <MDTypography variant="h6" fontWeight="bold">
                        기본 정보
                      </MDTypography>
                    </MDBox>

                    {/* 사용자 ID */}
                    <MDBox display="flex" alignItems="flex-start" mb={2.5}>
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#2196f3', fontSize: '1.25rem' }}>badge</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          사용자 ID
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium" sx={{ color: '#2196f3' }}>
                          {user.id || "-"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* 이메일 */}
                    <MDBox display="flex" alignItems="flex-start" mb={2.5}>
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#2196f3', fontSize: '1.25rem' }}>email</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          이메일
                        </MDTypography>
                        {this.state.isEditing ? (
                          <MDInput
                            type="email"
                            value={this.state.editEmail}
                            onChange={(e: any) => this.setState({ editEmail: e.target.value })}
                            fullWidth
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <MDTypography variant="body1" fontWeight="medium">
                            {user.email || "-"}
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* 전화번호 */}
                    <MDBox display="flex" alignItems="flex-start">
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#2196f3', fontSize: '1.25rem' }}>phone</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          전화번호
                        </MDTypography>
                        {this.state.isEditing ? (
                          <MDInput
                            type="tel"
                            value={this.state.editMobile}
                            onChange={(e: any) => this.setState({ editMobile: e.target.value })}
                            fullWidth
                            sx={{ mt: 0.5 }}
                          />
                        ) : (
                          <MDTypography variant="body1" fontWeight="medium">
                            {user.mobile || "-"}
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* 역할 및 권한 */}
                <Grid item xs={12} md={6}>
                  <MDBox
                    sx={{
                      backgroundColor: cardBg,
                      borderRadius: 2,
                      padding: 3,
                      height: '100%',
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <MDBox display="flex" alignItems="center" mb={3}>
                      <Icon sx={{ fontSize: '1.5rem', color: '#9c27b0', mr: 1 }}>admin_panel_settings</Icon>
                      <MDTypography variant="h6" fontWeight="bold">
                        역할 및 권한
                      </MDTypography>
                    </MDBox>

                    {/* 역할 */}
                    <MDBox display="flex" alignItems="flex-start" mb={2.5}>
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(156, 39, 176, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#9c27b0', fontSize: '1.25rem' }}>workspace_premium</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          역할
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {user.role?.toUpperCase() || "USER"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* 로그인 방식 */}
                    <MDBox display="flex" alignItems="flex-start" mb={2.5}>
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(156, 39, 176, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#9c27b0', fontSize: '1.25rem' }}>lock</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          로그인 방식
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {user.loginType ? `${user.loginType.toUpperCase()} (${(user.loginType.toLowerCase() === 'login_qr' || user.loginType.toLowerCase() === 'qr') ? 'QR 코드' :
                            (user.loginType.toLowerCase() === 'login_pin' || user.loginType.toLowerCase() === 'pin') ? 'PIN 코드' :
                              user.loginType.toLowerCase() === 'login_sns' ? '소셜 로그인' :
                                user.loginType.toLowerCase() === 'login_kakao' ? '카카오톡' : '기타'
                            })` : "-"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* 앱 버전 */}
                    <MDBox display="flex" alignItems="flex-start">
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: 'rgba(156, 39, 176, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: '#9c27b0', fontSize: '1.25rem' }}>smartphone</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          앱 버전
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {(user as any).appVersion || "-"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>

              {/* PIN 번호 표시 (PIN 로그인인 경우) */}
              {((user.loginType || user.LoginType || "").toLowerCase() === "pin" || (user.loginType || user.LoginType || "").toLowerCase() === "login_pin") && (user.pinCode || user.PinCode) && (
                <MDBox
                  sx={{
                    backgroundColor: cardBg,
                    borderRadius: 2,
                    padding: 3,
                    mb: 3,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <Icon sx={{ fontSize: '1.5rem', color: '#4caf50', mr: 1 }}>lock</Icon>
                    <MDTypography variant="h6" fontWeight="bold">
                      PIN 번호
                    </MDTypography>
                  </MDBox>
                  <MDBox textAlign="center" py={2}>
                    <MDTypography
                      variant="h2"
                      sx={{
                        fontFamily: "monospace",
                        letterSpacing: 12,
                        fontWeight: "bold",
                        color: "#4caf50",
                        textShadow: darkMode ? '0 0 20px rgba(76, 175, 80, 0.4)' : 'none',
                      }}
                    >
                      {user.pinCode || user.PinCode}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}

              {/* QR 코드 표시 (QR 로그인인 경우) */}
              {(() => {
                const loginType = (user.loginType || user.LoginType || "").toLowerCase();
                const hasQrToken = !!(user.qrToken || user.QrToken);

                if ((loginType === "qr" || loginType === "login_qr") && hasQrToken) {
                  return (
                    <MDBox
                      sx={{
                        backgroundColor: cardBg,
                        borderRadius: 2,
                        padding: 3,
                        mb: 3,
                        border: `1px solid ${borderColor}`,
                      }}
                    >
                      <MDBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ fontSize: '1.5rem', color: '#2196f3', mr: 1 }}>qr_code</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                          QR 코드
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" justifyContent="center" py={2}>
                        {this.state.qrCodeSvg ? (
                          <MDBox
                            sx={{
                              p: 2,
                              backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                              borderRadius: 2,
                              border: `3px solid #2196f3`,
                              boxShadow: darkMode
                                ? '0 4px 20px rgba(33, 150, 243, 0.3)'
                                : '0 4px 20px rgba(33, 150, 243, 0.2)',
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: this.state.qrCodeSvg }} />
                          </MDBox>
                        ) : (
                          <MDTypography variant="body2" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                            QR 코드 생성 중...
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  );
                }
                return null;
              })()}

              {/* 관심 치료 부위 섹션 */}
              <MDBox mb={3} sx={{ backgroundColor: cardBg, borderRadius: 2, padding: 3, border: `1px solid ${borderColor}` }}>
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Icon sx={{ fontSize: '1.5rem', color: '#ff9800', mr: 1 }}>accessibility_new</Icon>
                  <MDTypography variant="h6" fontWeight="bold">
                    관심 치료 부위
                  </MDTypography>
                </MDBox>

                {this.state.isEditing ? (
                  <MDBox>
                    {this.state.interestAreaCategories.map((category: any) => {
                      const partsByCategory = this.state.availableBodyParts.filter((p: any) => p.category === category.code);
                      if (partsByCategory.length === 0) return null;

                      return (
                        <MDBox key={category.code} mb={2}>
                          <MDTypography variant="subtitle2" fontWeight="bold" color="text" mb={1}>
                            {category.name}
                          </MDTypography>
                          <Grid container spacing={1}>
                            {partsByCategory.map((part: any) => (
                              <Grid item key={part.seqId} xs={6} sm={4} md={3}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={this.state.editInterestBodyPartIds.includes(part.seqId)}
                                      onChange={(e: any) => {
                                        const checked = e.target.checked;
                                        this.setState((prevState) => {
                                          if (checked) {
                                            return { editInterestBodyPartIds: [...prevState.editInterestBodyPartIds, part.seqId] };
                                          } else {
                                            return { editInterestBodyPartIds: prevState.editInterestBodyPartIds.filter((id) => id !== part.seqId) };
                                          }
                                        });
                                      }}
                                      name={part.name}
                                    />
                                  }
                                  label={<MDTypography variant="body2">{part.name}</MDTypography>}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </MDBox>
                      );
                    })}
                    {this.state.availableBodyParts.filter((p: any) => !p.category).length > 0 && (
                      <MDBox mb={2}>
                        <MDTypography variant="subtitle2" fontWeight="bold" color="text" mb={1}>
                          기타
                        </MDTypography>
                        <Grid container spacing={1}>
                          {this.state.availableBodyParts.filter((p: any) => !p.category).map((part: any) => (
                            <Grid item key={part.seqId} xs={6} sm={4} md={3}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={this.state.editInterestBodyPartIds.includes(part.seqId)}
                                    onChange={(e: any) => {
                                      const checked = e.target.checked;
                                      this.setState((prevState) => {
                                        if (checked) {
                                          return { editInterestBodyPartIds: [...prevState.editInterestBodyPartIds, part.seqId] };
                                        } else {
                                          return { editInterestBodyPartIds: prevState.editInterestBodyPartIds.filter((id) => id !== part.seqId) };
                                        }
                                      });
                                    }}
                                    name={part.name}
                                  />
                                }
                                label={<MDTypography variant="body2">{part.name}</MDTypography>}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <MDBox display="flex" flexWrap="wrap" gap={1}>
                    {user.interestBodyPartIds && user.interestBodyPartIds.length > 0 ? (
                      user.interestBodyPartIds.map((id: number) => {
                        const part = this.state.availableBodyParts.find((p: any) => p.seqId === id);
                        return (
                          <Chip
                            key={id}
                            label={part ? part.name : `Unknown (${id})`}
                            variant="outlined"
                            color="warning"
                            size="small"
                          />
                        );
                      })
                    ) : (
                      <MDTypography variant="body2" color="text">
                        등록된 관심 부위가 없습니다.
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </MDBox>

              {/* 수술 부위 정보 섹션 */}
              <MDBox mb={3} sx={{ backgroundColor: cardBg, borderRadius: 2, padding: 3, border: `1px solid ${borderColor}` }}>
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Icon sx={{ fontSize: '1.5rem', color: '#f44336', mr: 1 }}>medication</Icon>
                  <MDTypography variant="h6" fontWeight="bold">
                    수술 부위 정보
                  </MDTypography>
                </MDBox>

                {this.state.isEditing ? (
                  <MDBox>
                    <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                      <Autocomplete
                        options={this.state.availableSurgerySites}
                        getOptionLabel={(option) => option.siteName}
                        value={this.state.editSelectedSurgerySite}
                        onChange={(event, newValue) => {
                          this.setState({ editSelectedSurgerySite: newValue });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="수술 부위 선택"
                            variant="outlined"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                "& fieldset": {
                                  borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                              },
                              "& .MuiInputBase-input": {
                                color: darkMode ? "#fff" : "#000",
                              },
                            }}
                          />
                        )}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="수술 날짜"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={
                          this.state.editSelectedSurgeryDate
                            ? this.state.editSelectedSurgeryDate.toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          this.setState({ editSelectedSurgeryDate: date });
                        }}
                        sx={{
                          width: 180,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                            "& fieldset": {
                              borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                          },
                          "& .MuiInputBase-input": {
                            color: darkMode ? "#fff" : "#000",
                          },
                          "& input::-webkit-calendar-picker-indicator": {
                            display: "block",
                            filter: darkMode ? "invert(1)" : "none",
                            cursor: "pointer",
                          },
                        }}
                      />
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={this.handleAddSurgerySite}
                        disabled={!this.state.editSelectedSurgerySite}
                      >
                        추가
                      </MDButton>
                    </MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {this.state.editLiveSurgerySites.map((site, index) => (
                        <Chip
                          key={index}
                          label={`${site.siteName} (${site.date ? site.date.toISOString().split("T")[0] : "날짜 없음"})`}
                          onDelete={() => this.handleRemoveSurgerySite(index)}
                          variant="outlined"
                          color="info"
                          size="small"
                        />
                      ))}
                    </MDBox>
                  </MDBox>
                ) : (
                  <MDBox display="flex" flexWrap="wrap" gap={1}>
                    {(user.surgerySites && user.surgerySites.length > 0) ? (
                      user.surgerySites.map((site: any) => {
                        const siteItem = this.state.availableSurgerySites?.find((as: any) => (as.seqId || as.SeqId) === site.surgerySiteSeqId);
                        const siteName = siteItem ? siteItem.siteName : `부위-${site.surgerySiteSeqId}`;
                        return (
                          <Chip
                            key={site.surgerySiteSeqId}
                            label={`${siteName} (${site.surgeryDate ? new Date(site.surgeryDate).toISOString().split("T")[0] : "날짜 없음"})`}
                            variant="outlined"
                            color="info"
                            size="small"
                          />
                        );
                      })
                    ) : (
                      <MDTypography variant="body2" color="text">
                        등록된 수술 부위가 없습니다.
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </MDBox>

              {/* 적용 정책 섹션 */}
              {
                (user.stepPolicies?.length > 0 || (user.videoPolicies || user.VideoPolicies)?.length > 0 || (user.challengePolicies || user.ChallengePolicies)?.length > 0 || (user.usePolicies || user.UsePolicies)?.length > 0 || (user.pointLimitPolicies && user.pointLimitPolicies.length > 0)) && (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" mb={2} color={darkMode ? "white" : "dark"}>
                      정책 설정
                    </MDTypography>

                    {/* 걸음수 포인트 적립 정책 */}
                    {(this.state.isEditing || user.stepPolicies?.length > 0) && (
                      <MDBox mb={2}>
                        {this.state.isEditing ? (
                          <Autocomplete
                            multiple
                            options={this.state.stepPolicies}
                            getOptionLabel={(option: any) => option.policyName || ""}
                            isOptionEqualToValue={(option: any, value: any) => option.seqId === value.seqId}
                            value={this.state.stepPolicies.filter((p: any) => this.state.editStepPolicySeqIds.includes(p.seqId))}
                            onChange={(_event: any, newValue: any[]) => {
                              // 시스템 정책은 제거 불가 - user.stepPolicies 기준 seqId 유지
                              const sysSet = new Set(
                                (user.stepPolicies || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                  .map((p: any) => p.seqId ?? p.SeqId)
                              );
                              const currentSysIds = this.state.editStepPolicySeqIds.filter((id) => sysSet.has(id));
                              const merged = Array.from(new Set([...newValue.map((v: any) => v.seqId), ...currentSysIds]));
                              this.setState({ editStepPolicySeqIds: merged });
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, onDelete, ...tagProps } = getTagProps({ index });
                                const sysSet = new Set(
                                  (user.stepPolicies || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                    .map((p: any) => p.seqId ?? p.SeqId)
                                );
                                const isSystem = sysSet.has(option.seqId);
                                return (
                                  <Chip
                                    key={key}
                                    label={isSystem ? `🔒 ${option.policyName}` : option.policyName}
                                    {...tagProps}
                                    {...(!isSystem ? { onDelete } : {})}
                                    size="small"
                                    {...(isSystem ? { sx: { backgroundColor: 'rgba(255, 152, 0, 0.2)', borderColor: 'rgba(255, 152, 0, 0.6)', color: '#ff9800' } } : {})}
                                  />
                                );
                              })
                            }
                            loading={this.state.loadingPolicies}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="걸음수 포인트 적립 정책 (다중 선택)"
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                    "& fieldset": {
                                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#000",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)'}`,
                              borderRadius: '4px',
                              padding: '16.5px 14px',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                              position: 'relative',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                left: '10px',
                                backgroundColor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : (theme?.palette?.background?.paper || '#ffffff'),
                                padding: '0 5px',
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              걸음수 포인트 적립 정책 (다중 선택)
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1}>
                              {user.stepPolicies.map((policy: any) => {
                                const isSystem = policy.isSystemPolicy ?? policy.IsSystemPolicy;
                                return (
                                  <MDBox
                                    key={policy.seqId}
                                    sx={{
                                      backgroundColor: isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.08)'),
                                      border: `1px solid ${isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.6)' : 'rgba(255, 152, 0, 0.5)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.5)' : 'rgba(25, 118, 210, 0.5)')}`,
                                      borderRadius: '16px',
                                      padding: '4px 12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <MDTypography variant="caption" sx={{ color: isSystem ? '#ff9800' : (darkMode ? '#90caf9' : '#1976d2') }}>
                                      {isSystem ? `🔒 ${policy.policyName}` : policy.policyName}
                                    </MDTypography>
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    {/* 영상시청 포인트 적립 정책 */}
                    {(this.state.isEditing || (user.videoPolicies || user.VideoPolicies)?.length > 0) && (
                      <MDBox mb={2}>
                        {this.state.isEditing ? (
                          <Autocomplete
                            multiple
                            options={this.state.videoPolicies}
                            getOptionLabel={(option: any) => option.policyName || ""}
                            isOptionEqualToValue={(option: any, value: any) => option.seqId === value.seqId}
                            value={this.state.videoPolicies.filter((p: any) => this.state.editVideoPolicySeqIds.includes(p.seqId))}
                            onChange={(_event: any, newValue: any[]) => {
                              // 시스템 정책은 제거 불가 - user.videoPolicies 기준 seqId 유지
                              const sysSet = new Set(
                                ((user.videoPolicies || user.VideoPolicies) || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                  .map((p: any) => p.seqId ?? p.SeqId)
                              );
                              const currentSysIds = this.state.editVideoPolicySeqIds.filter((id) => sysSet.has(id));
                              const merged = Array.from(new Set([...newValue.map((v: any) => v.seqId), ...currentSysIds]));
                              this.setState({ editVideoPolicySeqIds: merged });
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, onDelete, ...tagProps } = getTagProps({ index });
                                const sysSet = new Set(
                                  ((user.videoPolicies || user.VideoPolicies) || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                    .map((p: any) => p.seqId ?? p.SeqId)
                                );
                                const isSystem = sysSet.has(option.seqId);
                                return (
                                  <Chip
                                    key={key}
                                    label={isSystem ? `🔒 ${option.policyName}` : option.policyName}
                                    {...tagProps}
                                    {...(!isSystem ? { onDelete } : {})}
                                    size="small"
                                    {...(isSystem ? { sx: { backgroundColor: 'rgba(255, 152, 0, 0.2)', borderColor: 'rgba(255, 152, 0, 0.6)', color: '#ff9800' } } : {})}
                                  />
                                );
                              })
                            }
                            loading={this.state.loadingPolicies}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="영상시청 포인트 적립 정책 (다중 선택)"
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                    "& fieldset": {
                                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#000",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)'}`,
                              borderRadius: '4px',
                              padding: '16.5px 14px',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                              position: 'relative',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                left: '10px',
                                backgroundColor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : (theme?.palette?.background?.paper || '#ffffff'),
                                padding: '0 5px',
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              영상시청 포인트 적립 정책 (다중 선택)
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1}>
                              {(user.videoPolicies || user.VideoPolicies)?.map((policy: any) => {
                                const isSystem = policy.isSystemPolicy ?? policy.IsSystemPolicy;
                                return (
                                  <MDBox
                                    key={policy.seqId}
                                    sx={{
                                      backgroundColor: isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.08)'),
                                      border: `1px solid ${isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.6)' : 'rgba(255, 152, 0, 0.5)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.5)' : 'rgba(25, 118, 210, 0.5)')}`,
                                      borderRadius: '16px',
                                      padding: '4px 12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <MDTypography variant="caption" sx={{ color: isSystem ? '#ff9800' : (darkMode ? '#90caf9' : '#1976d2') }}>
                                      {isSystem ? `🔒 ${policy.policyName}` : policy.policyName}
                                      {(() => {
                                        const cv = policy.conditionValue ?? policy.ConditionValue;
                                        const ct = policy.conditionType ?? policy.ConditionType;
                                        if (cv == null) return '';
                                        if (ct === 'VIDEO_COMPLETE') return ` (${cv}%)`;
                                        if (ct === 'DAILY_GOAL') return ` (${cv}회)`;
                                        return ` (${cv})`;
                                      })()}
                                    </MDTypography>
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    {/* 챌린지 달성 포인트 적립 정책 */}
                    {(this.state.isEditing || (user.challengePolicies || user.ChallengePolicies)?.length > 0) && (
                      <MDBox mb={2}>
                        {this.state.isEditing ? (
                          <Autocomplete
                            multiple
                            options={this.state.challengePolicies}
                            getOptionLabel={(option: any) => option.policyName || ""}
                            isOptionEqualToValue={(option: any, value: any) => option.seqId === value.seqId}
                            value={this.state.challengePolicies.filter((p: any) => this.state.editChallengePolicySeqIds.includes(p.seqId))}
                            onChange={(_event: any, newValue: any[]) => {
                              // 시스템 정책은 제거 불가 - user.challengePolicies 기준 seqId 유지
                              const sysSet = new Set(
                                ((user.challengePolicies || user.ChallengePolicies) || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                  .map((p: any) => p.seqId ?? p.SeqId)
                              );
                              const currentSysIds = this.state.editChallengePolicySeqIds.filter((id) => sysSet.has(id));
                              const merged = Array.from(new Set([...newValue.map((v: any) => v.seqId), ...currentSysIds]));
                              this.setState({ editChallengePolicySeqIds: merged });
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, onDelete, ...tagProps } = getTagProps({ index });
                                const sysSet = new Set(
                                  ((user.challengePolicies || user.ChallengePolicies) || []).filter((p: any) => p.isSystemPolicy || p.IsSystemPolicy)
                                    .map((p: any) => p.seqId ?? p.SeqId)
                                );
                                const isSystem = sysSet.has(option.seqId);
                                return (
                                  <Chip
                                    key={key}
                                    label={isSystem ? `🔒 ${option.policyName}` : option.policyName}
                                    {...tagProps}
                                    {...(!isSystem ? { onDelete } : {})}
                                    size="small"
                                    {...(isSystem ? { sx: { backgroundColor: 'rgba(255, 152, 0, 0.2)', borderColor: 'rgba(255, 152, 0, 0.6)', color: '#ff9800' } } : {})}
                                  />
                                );
                              })
                            }
                            loading={this.state.loadingPolicies}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="챌린지 달성 포인트 적립 정책 (다중 선택)"
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                    "& fieldset": {
                                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#000",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)'}`,
                              borderRadius: '4px',
                              padding: '16.5px 14px',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                              position: 'relative',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                left: '10px',
                                backgroundColor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : (theme?.palette?.background?.paper || '#ffffff'),
                                padding: '0 5px',
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              챌린지 달성 포인트 적립 정책 (다중 선택)
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1}>
                              {(user.challengePolicies || user.ChallengePolicies)?.map((policy: any) => {
                                const isSystem = policy.isSystemPolicy ?? policy.IsSystemPolicy;
                                return (
                                  <MDBox
                                    key={policy.seqId}
                                    sx={{
                                      backgroundColor: isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.08)'),
                                      border: `1px solid ${isSystem
                                        ? (darkMode ? 'rgba(255, 152, 0, 0.6)' : 'rgba(255, 152, 0, 0.5)')
                                        : (darkMode ? 'rgba(66, 165, 245, 0.5)' : 'rgba(25, 118, 210, 0.5)')}`,
                                      borderRadius: '16px',
                                      padding: '4px 12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <MDTypography variant="caption" sx={{ color: isSystem ? '#ff9800' : (darkMode ? '#90caf9' : '#1976d2') }}>
                                      {isSystem ? `🔒 ${policy.policyName}` : policy.policyName}
                                    </MDTypography>
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    {/* 포인트 사용 정책 */}
                    {(this.state.isEditing || (user.usePolicies || user.UsePolicies)?.length > 0) && (
                      <MDBox mb={2}>
                        {this.state.isEditing ? (
                          <Autocomplete
                            multiple
                            options={this.state.usePolicies}
                            getOptionLabel={(option: any) => option.policyName || ""}
                            isOptionEqualToValue={(option: any, value: any) => option.seqId === value.seqId}
                            value={this.state.usePolicies.filter((p: any) => this.state.editUsePolicySeqIds.includes(p.seqId))}
                            onChange={(_event: any, newValue: any[]) => {
                              this.setState({ editUsePolicySeqIds: newValue.map((v: any) => v.seqId) })
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                  <Chip
                                    key={key}
                                    label={option.policyName}
                                    {...tagProps}
                                    size="small"
                                  />
                                );
                              })
                            }
                            loading={this.state.loadingPolicies}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="포인트 사용 정책 (다중 선택)"
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                    "& fieldset": {
                                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#000",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)'}`,
                              borderRadius: '4px',
                              padding: '16.5px 14px',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                              position: 'relative',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                left: '10px',
                                backgroundColor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : (theme?.palette?.background?.paper || '#ffffff'),
                                padding: '0 5px',
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              포인트 사용 정책 (다중 선택)
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1}>
                              {(user.usePolicies || user.UsePolicies)?.map((policy: any) => (
                                <MDBox
                                  key={policy.seqId}
                                  sx={{
                                    backgroundColor: darkMode ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.08)',
                                    border: `1px solid ${darkMode ? 'rgba(66, 165, 245, 0.5)' : 'rgba(25, 118, 210, 0.5)'}`,
                                    borderRadius: '16px',
                                    padding: '4px 12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <MDTypography variant="caption" sx={{ color: darkMode ? '#90caf9' : '#1976d2' }}>
                                    {policy.policyName}
                                  </MDTypography>
                                </MDBox>
                              ))}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    {/* 포인트 제한 정책 섹션 */}
                    {(this.state.isEditing || (user.pointLimitPolicies && user.pointLimitPolicies.length > 0)) && (
                      <MDBox mb={2}>
                        {this.state.isEditing ? (
                          <Autocomplete
                            multiple
                            options={this.state.allPointLimitPolicies}
                            getOptionLabel={(option: any) => option.policyName || ""}
                            isOptionEqualToValue={(option: any, value: any) => option.seqId === value.seqId}
                            value={this.state.allPointLimitPolicies.filter((p: any) => this.state.editPointLimitPolicySeqIds.includes(p.seqId))}
                            onChange={(_event: any, newValue: any[]) => {
                              this.setState({ editPointLimitPolicySeqIds: newValue.map((v: any) => v.seqId) })
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                  <Chip
                                    key={key}
                                    label={option.policyName}
                                    {...tagProps}
                                    size="small"
                                  />
                                );
                              })
                            }
                            loading={this.state.loadingPolicies}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="포인트 제한 정책 (다중 선택)"
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                                    "& fieldset": {
                                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#000",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)'}`,
                              borderRadius: '4px',
                              padding: '16.5px 14px',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                              position: 'relative',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                left: '10px',
                                backgroundColor: darkMode ? (theme?.palette?.background?.default || '#1a1a1a') : (theme?.palette?.background?.paper || '#ffffff'),
                                padding: '0 5px',
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              포인트 제한 정책
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                              {user.pointLimitPolicies && user.pointLimitPolicies.map((policy: any) => (
                                <MDBox
                                  key={policy.seqId}
                                  sx={{
                                    backgroundColor: darkMode ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.08)',
                                    border: `1px solid ${darkMode ? 'rgba(66, 165, 245, 0.5)' : 'rgba(25, 118, 210, 0.5)'}`,
                                    borderRadius: '16px',
                                    padding: '4px 12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <MDTypography variant="caption" sx={{ color: darkMode ? '#90caf9' : '#1976d2' }}>
                                    {policy.policyName} ({policy.limitAmount}P/{policy.periodType})
                                  </MDTypography>
                                </MDBox>
                              ))}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )}
                  </MDBox>
                )
              }
            </MDBox>
          ) : (
            <MDBox p={3} display="flex" justifyContent="center">
              <MDTypography>사용자 정보를 불러올 수 없습니다.</MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: backgroundColor,
            padding: "16px 32px 24px 32px",
            borderTop: `1px solid ${borderColor}`,
          }}
        >
          {this.state.isEditing ? (
            <>
              <MDButton variant="outlined" color="secondary" onClick={this.handleCancelEdit} size="large">
                취소
              </MDButton>
              <MDButton variant="gradient" color="info" onClick={this.handleSaveEdit} size="large">
                저장
              </MDButton>
            </>
          ) : (
            <>
              <MDButton variant="outlined" color="info" onClick={this.handleEdit} size="large">
                편집
              </MDButton>
              <MDButton variant="gradient" color="dark" onClick={onClose} size="large">
                닫기
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

function UserDetailDialog(props: UserDetailDialogProps) {
  const [controller] = useMaterialUIController();
  const theme = useTheme();
  const { darkMode } = controller;

  // Dialog가 테마를 제대로 따르도록 ThemeProvider로 감싸기
  return (
    <ThemeProvider theme={theme}>
      <UserDetailDialogClass
        {...props}
        darkMode={darkMode}
        theme={theme}
      />
    </ThemeProvider>
  );
}

export default UserDetailDialog;

